import { SignIn } from '@clerk/clerk-react'
import { useEffect } from 'react'
import { useMobileDetection } from '@/hooks/useMobileDetection'

interface ClerkSignInWithMobileSupportProps {
  routing?: 'path' | 'virtual'
  path?: string
  signUpUrl?: string
  afterSignInUrl?: string
}

export function ClerkSignInWithMobileSupport(props: ClerkSignInWithMobileSupportProps) {
  const { isMobile, isLineAppLikely } = useMobileDetection()

  useEffect(() => {
    if (!isMobile) return

    // Override Clerk's OAuth button click behavior on mobile
    const intervalId = setInterval(() => {
      // Find LINE OAuth button
      const lineButton = document.querySelector('[data-provider="oauth_line"]') as HTMLElement
      
      if (lineButton && !lineButton.dataset.mobileEnhanced) {
        lineButton.dataset.mobileEnhanced = 'true'
        
        // For LINE on mobile, we want to ensure the OAuth flow opens in a way that triggers the app
        const originalOnClick = lineButton.onclick
        lineButton.onclick = function(e) {
          // LINE's OAuth page should automatically detect and open the app if installed
          // We just need to ensure it opens in a new window/tab which increases the chance
          // of the mobile OS intercepting and opening the LINE app
          if (isLineAppLikely) {
            e.preventDefault()
            // Get the OAuth URL from Clerk
            const form = lineButton.closest('form')
            if (form) {
              // Submit the form in a new window
              const target = form.target
              form.target = '_blank'
              form.submit()
              form.target = target || ''
            } else if (originalOnClick) {
              originalOnClick.call(this, e)
            }
          }
        }
      }

      // Find Facebook OAuth button
      const fbButton = document.querySelector('[data-provider="oauth_facebook"]') as HTMLElement
      
      if (fbButton && !fbButton.dataset.mobileEnhanced) {
        fbButton.dataset.mobileEnhanced = 'true'
        
        // Similar handling for Facebook
        const originalOnClick = fbButton.onclick
        fbButton.onclick = function(e) {
          if (isMobile) {
            e.preventDefault()
            const form = fbButton.closest('form')
            if (form) {
              const target = form.target
              form.target = '_blank'
              form.submit()
              form.target = target || ''
            } else if (originalOnClick) {
              originalOnClick.call(this, e)
            }
          }
        }
      }

      // Clear interval once both buttons are found and enhanced
      if (lineButton?.dataset.mobileEnhanced && fbButton?.dataset.mobileEnhanced) {
        clearInterval(intervalId)
      }
    }, 100)

    // Cleanup
    return () => clearInterval(intervalId)
  }, [isMobile, isLineAppLikely])

  return (
    <SignIn
      appearance={{
        elements: {
          rootBox: 'mx-auto',
          card: 'shadow-xl',
          headerTitle: 'hidden',
          headerSubtitle: 'hidden',
          socialButtonsBlockButton: 'flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50',
          socialButtonsBlockButtonText: 'font-medium',
          formFieldLabel: 'text-sm font-medium text-gray-700',
          formFieldInput: 'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500',
          formButtonPrimary: 'w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
          footerActionLink: 'text-blue-600 hover:text-blue-500',
        },
        layout: {
          socialButtonsPlacement: 'top',
          socialButtonsVariant: 'blockButton',
        },
      }}
      {...props}
    />
  )
}